import { useMemo, useState } from 'react';

import { IModifier } from 'features/menu/components/product-detail/modifier/types';
import { ProductCustomizationModifiers } from 'features/menu/components/product-detail/product-customization/product-customization-modifiers';
import { CustomizationRenderItem } from 'features/menu/components/product-detail/product-customization/types';
import { getComboSlotItems } from 'features/menu/components/product-detail/product-customization/utils';
import { SelectorContainer } from 'features/menu/components/product-detail/styled';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import { DEFAULT_SLOT_KEY } from 'features/menu/components/product-wizard/types';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';
import { useStoreContext } from 'features/store';

import { ListCardGroup } from 'components/list-card-group';
import { ProductCard } from 'components/product-card';
import { useModifierRedesignPhaseTwo } from 'experiments/use-modifier-redesign-phase-two';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { useMainMenuContext } from 'state/menu/main-menu';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { getEntryPrice, hasOptionEntries, stringifySanityImage } from 'state/unified-menu/utils';

import { getValidModifiersForSelection, mapModifierForCustomization } from '../modifier/utils';

export const ProductCustomizationItem = () => {
  const { indexedMenu } = useUnifiedMenuContext();
  const { currentFlowStep } = useProductWizardFlow();
  const { slotsSelections, selectComboSlotItem } = useProductWizardContext();
  const menuQueryOptions = useMenuQueryOptions();
  const { formatCalories } = useMenuCalories();

  const { store } = useStoreContext();
  const { activeDayParts } = useMainMenuContext();
  const storeHasBurgersForBreakfast = Boolean(store.hasBurgersForBreakfast);

  const slotKey = currentFlowStep.slotKey!;
  const selectedSlot = slotsSelections[slotKey];
  const selectedItemId = selectedSlot?.itemId;
  const [selectedRow, setSelectedRow] = useState<number>();

  const comboSlotItems: ILocalizedMenuEntry[] = useMemo(
    () =>
      slotKey === DEFAULT_SLOT_KEY
        ? []
        : getComboSlotItems({
            menu: indexedMenu,
            slotKey,
            posServiceMode: menuQueryOptions.posServiceMode,
            activeDayParts,
            storeHasBurgersForBreakfast,
          }),
    [
      slotKey,
      indexedMenu,
      menuQueryOptions.posServiceMode,
      activeDayParts,
      storeHasBurgersForBreakfast,
    ]
  );

  const modifiers = useMemo(() => {
    if (!selectedItemId) {
      return [];
    }

    const selectedItem = indexedMenu.get(selectedItemId);
    if (!hasOptionEntries(menuQueryOptions.posServiceMode, selectedItem)) {
      return [];
    }

    const mappedModifiers = selectedItem.options.entries.map(({ entryId, quantity }) =>
      mapModifierForCustomization(indexedMenu, entryId, menuQueryOptions.posServiceMode, quantity)
    );

    // filter if null values
    const filteredMods: IModifier[] = (mappedModifiers as Array<IModifier | null>).filter(
      (modifier): modifier is IModifier => !!modifier && !!modifier.multipliers?.length
    );

    const validModifiers = getValidModifiersForSelection(filteredMods);
    return validModifiers;
  }, [indexedMenu, menuQueryOptions.posServiceMode, selectedItemId]);

  // Log Control experiment exposure if necessary
  const hasMultipleComboSlotOptions = comboSlotItems.length > 1;
  const hasModifiers = modifiers.length > 0;
  const skipExposureLogging = !(hasMultipleComboSlotOptions && hasModifiers);
  useModifierRedesignPhaseTwo(skipExposureLogging);

  return (
    <>
      {/* If it has less than 2 entries, we render the modifiers component. */}
      {comboSlotItems.length < 2 ? null : (
        <SelectorContainer>
          <ListCardGroup
            items={comboSlotItems}
            selectedRow={selectedRow}
            renderItem={({ item, index, rowIndex }: CustomizationRenderItem) => (
              <ProductCard
                key={`item-${item.id}-${index}`}
                isSelected={item.id === selectedSlot?.itemId}
                productProps={{
                  name: item.name?.locale,
                  price: getEntryPrice(item, menuQueryOptions.posServiceMode),
                  calories: formatCalories(item.nutrition?.calories?.def),
                  image: stringifySanityImage(item.image),
                }}
                onProductPress={() => {
                  logButtonClick({
                    attributes: {
                      component: ClickEventComponentNames.PRODUCT_COMBO_SLOT_SELECTION_UPDATE,
                      name: item.name?.locale ?? '',
                      Id: item.id ?? '',
                    },
                  });
                  selectComboSlotItem(slotKey, item.id);
                  setSelectedRow(rowIndex);
                }}
              />
            )}
          />
        </SelectorContainer>
      )}
      {/* Render Modifiers if applicable */}
      <ProductCustomizationModifiers />
    </>
  );
};
